import { attachCampaign, openCampaignLink } from "../api/campaign";
import { getToken, hasValidToken } from "../api/storage";

// Campaign links: tarbaa.com/go/<slug> (counts the click, then redirects) or
// any page opened with ?c=<slug> (for ad platforms that want the final URL).
// The server counts the click against this browser's visitor id; once the
// visitor signs in, it credits their account with every link this browser
// came through in the last 30 days. This module is the only owner of that
// storage (the visitor id and the last "touch"); everything else goes
// through it. Nothing here ever throws to its callers.
const VISITOR_KEY = "tarbaa_vid";
const TOUCH_KEY = "tarbaa_campaign_touch";
const TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// The shapes the server accepts.
const VISITOR_ID = /^[A-Za-z0-9_-]{8,64}$/;
const SLUG = /^[a-z0-9][a-z0-9-]{1,59}$/;

const ID_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
const ID_LENGTH = 22;

const NOT_FOUND = { found: false, destination: "/" };

export const normalizeSlug = (raw) => {
  const slug = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return SLUG.test(slug) ? slug : "";
};

// 22 characters from a 64-letter alphabet (256 is a multiple of 64, so
// `byte & 63` stays uniform). Math.random only where crypto is missing.
const makeVisitorId = () => {
  let bytes = null;
  try {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      bytes = crypto.getRandomValues(new Uint8Array(ID_LENGTH));
    }
  } catch (_) {}
  let id = "";
  for (let i = 0; i < ID_LENGTH; i++) {
    const n = bytes ? bytes[i] : Math.floor(Math.random() * 256);
    id += ID_ALPHABET[n & 63];
  }
  return id;
};

// Kept in memory as well, so the id stays the same for the page's life when
// storage is unavailable (private mode, blocked site data).
let memoryVisitorId = null;

export const getVisitorId = () => {
  try {
    const stored = localStorage.getItem(VISITOR_KEY);
    if (stored && VISITOR_ID.test(stored)) return stored;
  } catch (_) {
    if (!memoryVisitorId) memoryVisitorId = makeVisitorId();
    return memoryVisitorId;
  }
  if (!memoryVisitorId) memoryVisitorId = makeVisitorId();
  try {
    localStorage.setItem(VISITOR_KEY, memoryVisitorId);
  } catch (_) {}
  return memoryVisitorId;
};

// The last campaign link this browser came through: { slug, at }. Also kept
// in memory, so a sign-in later in this page load still attaches when
// storage can't hold it.
let memoryTouch = null;
// The session this page load already attached (null: none yet, or a new
// link came in since).
let attachedFor = null;

const isFresh = (touch) =>
  !!touch &&
  typeof touch.slug === "string" &&
  typeof touch.at === "number" &&
  Date.now() - touch.at < TOUCH_TTL_MS;

const readTouch = () => {
  try {
    const raw = localStorage.getItem(TOUCH_KEY);
    if (raw) {
      const touch = JSON.parse(raw);
      if (isFresh(touch)) return touch;
      localStorage.removeItem(TOUCH_KEY);
    }
  } catch (_) {}
  return isFresh(memoryTouch) ? memoryTouch : null;
};

const writeTouch = (slug) => {
  memoryTouch = { slug, at: Date.now() };
  try {
    localStorage.setItem(TOUCH_KEY, JSON.stringify(memoryTouch));
  } catch (_) {}
  // A new link to credit: the next attach call goes out again.
  attachedFor = null;
};

// Only the referring site's host name, never its path or query.
const referrerHost = () => {
  try {
    return document.referrer ? new URL(document.referrer).hostname : "";
  } catch (_) {
    return "";
  }
};

// The server only sends same-site paths; never follow anything else.
const sameSitePath = (raw) =>
  typeof raw === "string" &&
  raw.startsWith("/") &&
  !raw.startsWith("//") &&
  !raw.startsWith("/\\")
    ? raw
    : "/";

// slug|test -> the request, so a link counts once per page load (a /go/
// redirect landing on a ?c= page, React's double effects in development).
const opened = new Map();

const sendOpen = async (slug, test, key) => {
  try {
    const body = { slug, visitorId: getVisitorId() };
    const referrer = referrerHost();
    if (referrer) body.referrer = referrer;
    if (test) body.test = true;
    const res = (await openCampaignLink(body)) || {};
    // A test open counts nothing, so there is nothing to credit later.
    if (!test && (res.counted || res.found)) writeTouch(slug);
    return { ...res, destination: sameSitePath(res.destination) };
  } catch (_) {
    // Not counted: the same link later in this page load may try again.
    opened.delete(key);
    return NOT_FOUND;
  }
};

// Count a visit through a campaign link. Resolves to the server's answer
// with a destination that is always a same-site path.
export const openCampaign = (raw, { test = false } = {}) => {
  const slug = normalizeSlug(raw);
  if (!slug) return Promise.resolve(NOT_FOUND);
  const key = `${slug}|${test ? 1 : 0}`;
  if (!opened.has(key)) opened.set(key, sendOpen(slug, !!test, key));
  return opened.get(key);
};

// Credit the signed-in account with this browser's campaign links. Only when
// there is a recent touch, and once per session per page load. An expired
// token is skipped (its 401 would bounce the visitor to the login page).
export const attachCampaignVisitor = async () => {
  let token = null;
  try {
    token = hasValidToken() ? getToken() : null;
  } catch (_) {}
  if (!token || attachedFor === token || !readTouch()) return null;
  attachedFor = token;
  try {
    return await attachCampaign(getVisitorId());
  } catch (_) {
    // Let a later sign-in or page load try again.
    if (attachedFor === token) attachedFor = null;
    return null;
  }
};

// Any page opened with ?c=<slug>: count it without leaving the page (?test=1
// counts nothing, as on /go/). The /go/ page opens its own link.
export const captureCampaignFromUrl = (pathname, search) => {
  if (/^\/go(\/|$)/.test(pathname)) return;
  let slug = "";
  let test = false;
  try {
    const params = new URLSearchParams(search);
    slug = normalizeSlug(params.get("c"));
    test = params.get("test") === "1";
  } catch (_) {}
  if (!slug) return;
  openCampaign(slug, { test }).then(() => attachCampaignVisitor());
};
